import React from 'react';

const CertificateDetails = ({ certificate }) => {
  if (!certificate) return null;

  const getStatusInfo = (status) => {
    const statusText = typeof status === 'boolean'
      ? (status ? 'active' : 'inactive')
      : String(status).toLowerCase();

    switch (statusText) {
      case 'active':
      case 'true':
        return { label: 'ACTIVE', bg: '#16a34a', text: '#fff' };
      case 'expired':
      case 'inactive':
      case 'false':
        return { label: 'INACTIVE', bg: '#dc2626', text: '#fff' };
      case 'suspended':
        return { label: 'SUSPENDED', bg: '#d97706', text: '#fff' };
      default:
        return { label: String(status).toUpperCase(), bg: '#16a34a', text: '#fff' };
    }
  };

  const getStatusText = (status) => {
    if (typeof status === 'boolean') return status ? 'ACTIVE' : 'INACTIVE';
    return String(status).toUpperCase();
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Not specified';
      if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        return dateString;
      }
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
      }
      return dateString;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const statusInfo = getStatusInfo(certificate.status);

  const labelStyle = { fontSize: '11px', fontWeight: '700', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '4px' };
  const valueStyle = { fontSize: '14px', color: '#ffffff', fontWeight: '500' };

  return (
    <div style={{ marginTop: '28px' }}>
      {/* Result Header Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0d2347 100%)',
        borderRadius: '10px',
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '24px',
      }}>
        <div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '6px' }}>
            Verified Entity
          </div>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#ffffff', margin: 0, lineHeight: 1.4 }}>
            {certificate.company_name}
          </h2>
        </div>
        {certificate.status !== undefined && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: statusInfo.bg,
            color: statusInfo.text,
            borderRadius: '20px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '0.6px',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {statusInfo.label}
          </span>
        )}
      </div>

      {/* Details Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px', marginBottom: '20px', background: '#111111', borderRadius: '8px', padding: '16px 20px' }}>
        <div>
          <div style={labelStyle}>Certificate No.</div>
          <div style={{ ...valueStyle, fontFamily: 'monospace', fontSize: '13.5px', color: '#c9a227', fontWeight: '600' }}>
            {certificate.certificate_no}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Name of Company</div>
          <div style={valueStyle}>{certificate.company_name}</div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={labelStyle}>Address of Company</div>
          <div style={valueStyle}>{certificate.company_address || 'Not specified'}</div>
        </div>
      </div>

      {/* Gold divider */}
      <div style={{ height: '2px', background: 'linear-gradient(90deg, #c9a227, #e8c547, #c9a227)', borderRadius: '2px', marginBottom: '20px', opacity: 0.6 }} />

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px 24px', marginBottom: '20px' }}>
        <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Date of Issue</div>
          <div style={valueStyle}>{formatDate(certificate.issue_date)}</div>
        </div>
        <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Expiry Date</div>
          <div style={valueStyle}>{formatDate(certificate.expiry_date)}</div>
        </div>
        <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Status</div>
          <div style={{ ...valueStyle, color: statusInfo.bg, fontWeight: '700' }}>{getStatusText(certificate.status)}</div>
        </div>
        <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '14px 16px' }}>
          <div style={labelStyle}>Certificate Standard</div>
          <div style={valueStyle}>{certificate.certificate_standard || 'Not specified'}</div>
        </div>
      </div>

      {/* Scope */}
      <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '14px 16px' }}>
        <div style={labelStyle}>Certification Scope</div>
        <div style={valueStyle}>{certificate.scope || certificate.certification_scope || 'Not specified'}</div>
      </div>
    </div>
  );
};

export default CertificateDetails;
